import { useCallback, useState } from 'react';

import { Alert, Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { Spinner } from '@provider-portal/common/components/Spinner/Spinner';

import { NetworkRequestState } from '@bolteu/redux-session';

import { LoginErrorType } from 'root/screens/Login/Login';

interface LoginFormFields {
  username: string;
  password: string;
}

interface LoginFormProps {
  strings: Record<
    'wrongCredentials' | 'accountLocked' | 'username' | 'password' | 'rememberMe' | 'login',
    string
  >;
  onSubmit: (payload: LoginFormFields) => void;
  authError: LoginErrorType | undefined;
  authRequestState: NetworkRequestState | undefined;
  accountRequestState: NetworkRequestState;
}

export function LoginForm({
  strings,
  onSubmit,
  authError,
  authRequestState,
  accountRequestState,
}: LoginFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onSubmit({ username, password });
    },
    [onSubmit, username, password],
  );

  const handleUsernameChange = useCallback((e) => {
    setUsername(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  const authErrorString = () => {
    if (!authError) {
      return undefined;
    }
    switch (authError) {
      case LoginErrorType.BAD_CREDENTIALS:
        return strings.wrongCredentials;
      case LoginErrorType.ACCOUNT_LOCKED:
        return strings.accountLocked;
      default:
        return strings.wrongCredentials;
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="login-form">
      {authError ? (
        <Alert variant="filled" severity="error" sx={{ mt: 2, mb: 2 }}>
          {authErrorString()}
        </Alert>
      ) : null}
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="username"
        label={strings.username}
        name="username"
        autoComplete="username"
        autoFocus
        onChange={handleUsernameChange}
      />
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        name="password"
        label={strings.password}
        type="password"
        id="password"
        autoComplete="current-password"
        onChange={handlePasswordChange}
      />
      <Box mt={2} mb={1}>
        <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label={strings.rememberMe}
        />
      </Box>
      <Button type="submit" fullWidth variant="contained" color="primary">
        {authRequestState === NetworkRequestState.waiting ||
        accountRequestState === NetworkRequestState.waiting ? (
          <Spinner />
        ) : (
          <span>{strings.login}</span>
        )}
      </Button>
    </form>
  );
}
